import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAdmin } from '../lib/currentUser';
import {
  LayoutGrid, Users, FileText, ShieldCheck, Star,
  BarChart3, Files, Settings, Tag, ChevronRight,
} from 'lucide-react';

// ============================================================
// FUSION SHARED SIDEBAR (Part A5) — one component, rendered from a
// per-product config. Everything here except the config below is the
// cross-product contract shared with Quotezilla; the SUPPLY_PILOT
// config (Part B1) is the ONLY thing that differs between products.
// ============================================================

// Supply Pilot product config (Part B1). `adminOnly` gates a section
// or item to admin/super_user — it's the app's real access rule laid
// over the shared config shape (the abstract schema in A8 has no such
// field, but every product needs role-scoping and it doesn't change
// the shared render path). `exact` matches the pathname exactly so
// /admin doesn't also light up for /admin/tags.
const SECTIONS = [
  { title: 'Main menu', items: [
    { icon: LayoutGrid,  label: 'Dashboard',  href: '/dashboard' },
    { icon: Users,       label: 'Suppliers',  href: '/suppliers' },
    { icon: FileText,    label: 'Briefs',     href: '/briefs' },
    { icon: ShieldCheck, label: 'Compliance', href: '/compliance', adminOnly: true },
    // No Debriefs item, deliberately (Rob, 2026-08-13). A debrief is raised
    // from the brief it came out of and read on the brief and the supplier
    // record — it isn't a section people go to cold, and a nav entry implied
    // it was. /debriefs still exists as a route for the index and for links.
    { icon: Star,        label: 'Pinned',     href: '/pinned' },
  ] },
  { title: 'System', adminOnly: true, items: [
    { icon: BarChart3, label: 'Reporting', href: '/reporting' },
    { icon: Files,     label: 'Compliance Configuration', href: '/documents' },
    { icon: Settings,  label: 'Admin',     href: '/admin', exact: true },
    { icon: Tag,       label: 'Tags',      href: '/admin/tags' },
  ] },
];

// Shared icon spec (A6): identical set (lucide), size 18, stroke ~1.9.
const ICON_SIZE = 18;
const ICON_STROKE = 1.9;

// SectionHeader (§3) — one style; only the label text varies per
// product. 11px / 600 / 0.14em / uppercase / #6E7468.
function SectionHeader({ children }) {
  return (
    <p className="hidden xl:block px-3 mt-6 mb-2 first:mt-0 font-['Metropolis'] text-[11px] font-semibold tracking-[0.14em] uppercase text-[#6E7468]">
      {children}
    </p>
  );
}

// Count pill (§3) — neutral by default (#2A2C26 bg / #C9CCC3 text);
// green is reserved for a genuine call-to-attention, not a plain count.
function CountPill({ count, label }) {
  return (
    <span
      aria-label={`${label}, ${count} item${count === 1 ? '' : 's'}`}
      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#2A2C26] text-[#C9CCC3] font-['Metropolis'] text-[11px] font-semibold tabular-nums">
      {count}
    </span>
  );
}

// NavItem (§3) — one row spec used everywhere. Default: icon + label
// #C9CCC3, 40px tall, 8px radius. Hover: white/4% wash. Active: a
// green-tint fill (fusion-green/14%), a 3px lime left accent, a green
// icon, and a pale-green #EAF3D6 label. Supports an optional count
// pill and one level of children via a disclosure chevron + guide line.
function NavItem({ item, active, childActive, onNavigate, child = false }) {
  const Icon = item.icon;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  // Open when a descendant is active, or on user toggle.
  const [open, setOpen] = useState(!!childActive);
  const controlsId = hasChildren ? `nav-group-${item.label.replace(/\s+/g, '-').toLowerCase()}` : undefined;

  const rowBase = child
    ? "w-full flex items-center gap-2.5 pl-3 pr-2.5 py-1.5 rounded-lg font-['Metropolis'] text-[13px] transition-colors"
    // Centred while collapsed, left-aligned once the label returns.
    : "w-full flex items-center justify-center xl:justify-start gap-0 xl:gap-3 px-0 xl:px-3 h-10 rounded-lg font-['Metropolis'] text-sm transition-colors";
  const stateClass = active
    ? 'relative bg-fusion-green/[0.14] text-[#EAF3D6] font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-fusion-green'
    : 'text-[#C9CCC3] font-medium hover:bg-white/[0.04]';

  const handleClick = () => {
    if (hasChildren) { setOpen(o => !o); return; }
    if (item.href) onNavigate(item.href);
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        aria-current={active ? 'page' : undefined}
        // The only affordance left when the label is hidden. Always set, so it
        // costs nothing to keep and cannot fall out of step with the label.
        title={item.label}
        aria-expanded={hasChildren ? open : undefined}
        aria-controls={controlsId}
        className={`${rowBase} ${stateClass}`}>
        <Icon
          size={child ? 16 : ICON_SIZE}
          strokeWidth={ICON_STROKE}
          className={active ? 'text-fusion-green flex-shrink-0' : 'text-[#C9CCC3] flex-shrink-0'}
          aria-hidden="true" />
        <span className="hidden xl:inline truncate text-left">{item.label}</span>
        {typeof item.count === 'number' && <CountPill count={item.count} label={item.label} />}
        {hasChildren && (
          <ChevronRight
            size={16}
            strokeWidth={2}
            aria-hidden="true"
            className={`hidden xl:block ml-auto flex-shrink-0 text-sb-muted transition-transform ${open ? 'rotate-90' : ''}`} />
        )}
      </button>

      {/* Child group (max one level, A6) — indented with a left guide
          line (--sb-guide). Supports caption dividers between rows. */}
      {hasChildren && open && (
        <div id={controlsId} className="mt-1 ml-[22px] pl-3 border-l border-sb-guide flex flex-col gap-0.5">
          {item.children.map((c, i) =>
            c.divider ? (
              <p key={`d-${i}`} className="px-3 pt-2 pb-1 font-['Metropolis'] text-[9px] font-bold tracking-[0.18em] uppercase text-sb-section">
                {c.divider}
              </p>
            ) : (
              <NavItem
                key={c.href || c.label}
                item={c}
                active={c.__active}
                onNavigate={onNavigate}
                child />
            )
          )}
        </div>
      )}
    </div>
  );
}

export default function SidebarNav({ currentInternalUser }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userIsAdmin = isAdmin(currentInternalUser);

  const isActive = (item) =>
    item.exact
      ? pathname === item.href
      : pathname === item.href || pathname.startsWith(`${item.href}/`);
  const anyChildActive = (item) =>
    Array.isArray(item.children) && item.children.some(c => c.href && isActive(c));

  return (
    <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-5 thin-scroll thin-scroll--dark">
      {SECTIONS.map(section => {
        if (section.adminOnly && !userIsAdmin) return null;
        const items = section.items.filter(it => !it.adminOnly || userIsAdmin);
        if (items.length === 0) return null;
        return (
          // data-otto-navsection="main" scopes the Otto perch-down-nav shot to
          // the Main-menu rows only — an unscoped selector also dips the System
          // rows (Reporting/Documents/Admin). See OttoRoamer resolveNavTarget.
          <div
            key={section.title}
            className="mb-1"
            data-otto-navsection={section.title === 'Main menu' ? 'main' : undefined}>
            <SectionHeader>{section.title}</SectionHeader>
            {items.map(item => {
              // Precompute child active flags so the recursive NavItem
              // doesn't need route context.
              const decorated = Array.isArray(item.children)
                ? { ...item, children: item.children.map(c => c.divider ? c : { ...c, __active: isActive(c) }) }
                : item;
              return (
                <NavItem
                  key={item.href || item.label}
                  item={decorated}
                  active={isActive(item)}
                  childActive={anyChildActive(item)}
                  onNavigate={navigate} />
              );
            })}
          </div>
        );
      })}
    </nav>
  );
}
